import classNames from 'classnames';
import { useEffect, useState } from 'react';

import { HockeyMatchControl } from 'src/types/matchControl';
import { CardTypes } from 'src/types/matchControl/card';
import { Clock } from 'src/types/matchControl/clock';
import { Contestant, HockeyContestant } from 'src/types/matchControl/contestant';
import { Control } from 'src/types/matchControl/control';
import { ShootOut as ShootOutType } from 'src/types/matchControl/shootOut';

import { ShootOut } from '../../components';
import ChildClock from '../../components/ChildClock';
import ClockRow from '../../components/ClockRow';
import { LineUp } from '../../components/LineUp';
import PenaltyClockSection from '../../components/PenaltyClockSection';
import Period from '../../components/Period';
import styles from '../../MatchControl.module.scss';
import { MatchControlVariant } from '../../types';

import HockeyCards from './HockeyCards';
import VideoReferralComponent from './HockeyVideoReferral';
import ScoreRow from './ScoreRow';

const SHOOT_OUT_ID = 'shootOut';

type Props = {
  matchControl: HockeyMatchControl;
  matchControlGraphics: Control[];
  handleDispatchControl: (control?: Control) => void;
  handleStartTime: (clock: Clock) => void;
  handleStopTime: (clock: Clock) => void;
  handleChangeTime: (clock: Clock, time: number) => void;
  handleChangeShortName: (contestant: HockeyContestant, name: string) => void;
  handleChangeScore: (contestant: HockeyContestant, amount: number) => void;
  handleChangeCards: (contestant: HockeyContestant, card: CardTypes, amount: number) => void;
  handleVideoReferral: (contestant: HockeyContestant, value: boolean) => void;
  handleChangeColor: (
    contestant: HockeyContestant,
    color: 'primaryColor' | 'secondaryColor',
    value: string
  ) => void;
  disabled?: boolean;
  variant?: MatchControlVariant;
  handleUpdateMatchControl: (matchControl: HockeyMatchControl) => void;
  handleUpdateShootOut: (shootOut: ShootOutType) => void;
  handleCreateClock: (type: Clock['type'], contestantId?: Contestant['id']) => void;
  handleDeleteClock: (id: Clock['id']) => void;
};

const Hockey = ({
  matchControl,
  matchControlGraphics,
  handleChangeColor,
  handleChangeShortName,
  handleChangeScore,
  handleChangeCards,
  handleVideoReferral,
  handleChangeTime,
  handleDispatchControl,
  handleStartTime,
  handleStopTime,
  handleUpdateMatchControl,
  handleUpdateShootOut,
  handleCreateClock,
  handleDeleteClock,
  variant = 'default',
}: Props) => {
  const { clocks, contestants, shootOut } = matchControl;
  const [mainClock, shotClock, shootoutClock] = clocks;
  const [home, away] = contestants;
  const [shootOutState, setShootOutState] = useState<ShootOutType | undefined>(shootOut);

  useEffect(() => {
    setShootOutState(shootOut);
  }, [matchControl.shootOut]);

  return (
    <div
      className={classNames(styles.MainWrapper, variant === 'default' && styles.MainWrapperCompact)}
    >
      <div className={styles.ItemWrapper}>
        <ClockRow
          mainClock={mainClock}
          matchControlGraphics={matchControlGraphics}
          isCompact={variant === 'default'}
          handleStartTime={handleStartTime}
          handleStopTime={handleStopTime}
          handleDispatchControl={handleDispatchControl}
          handleChangeTime={handleChangeTime}
        />
      </div>

      <div className={styles.Divider} />

      <div className={styles.ItemWrapper}>
        <ScoreRow
          home={home}
          away={away}
          isCompact={variant === 'default'}
          handleChangeScore={handleChangeScore}
          handleChangeColor={handleChangeColor}
          handleChangeShortName={handleChangeShortName}
        />
      </div>

      <div className={styles.Divider} />

      <HockeyCards home={home} away={away} handleChangeCards={handleChangeCards} />

      <div className={styles.Divider} />

      <PenaltyClockSection
        away={away}
        home={home}
        handleCreateClock={handleCreateClock}
        handleDeleteClock={handleDeleteClock}
      />

      <div className={styles.Divider} />

      <VideoReferralComponent home={home} away={away} handleVideoReferral={handleVideoReferral} />

      <div className={styles.Divider} />

      <div className="flex items-center gap-2">
        <div className="flex-auto min-w-0 font-semibold">Period</div>
        <div>
          <Period
            period={matchControl.period}
            isCompact={variant === 'default'}
            handleAddPeriod={() =>
              handleUpdateMatchControl({ ...matchControl, period: matchControl.period + 1 })
            }
            handleRemovePeriod={() =>
              handleUpdateMatchControl({
                ...matchControl,
                period: Math.max(1, matchControl.period - 1),
              })
            }
          />
        </div>
      </div>

      {shotClock && (
        <ChildClockRow
          name="Shot clock"
          clock={shotClock}
          handleStartTime={handleStartTime}
          handleStopTime={handleStopTime}
        />
      )}

      {shootoutClock && (
        <ChildClockRow
          name="Shoot-out clock"
          clock={shootoutClock}
          handleStartTime={handleStartTime}
          handleStopTime={handleStopTime}
        />
      )}

      <div className={styles.Divider} />

      <ShootOut
        hasShootOut={matchControl.hasShootOut}
        handleToggleShootOut={() =>
          handleUpdateMatchControl({ ...matchControl, hasShootOut: !matchControl.hasShootOut })
        }
        isLive={Boolean(
          matchControlGraphics?.some(
            (control) => control.controlId === SHOOT_OUT_ID && control.live
          )
        )}
        handleClickLive={() =>
          handleDispatchControl(matchControlGraphics?.find((c) => c.controlId === SHOOT_OUT_ID))
        }
        shootOutTaker={shootOutState?.taker || 'home'}
        home={{ shortName: home.shortName, attempts: shootOutState?.home || [] }}
        away={{ shortName: away.shortName, attempts: shootOutState?.away || [] }}
        handleUpdateShootOut={(shootOut) => setShootOutState(shootOut)}
        handleClickUpdate={() => shootOutState && handleUpdateShootOut(shootOutState)}
      />

      <div className={styles.Divider} />

      <LineUp home={home} away={away} />
    </div>
  );
};

type ChildClockRowProps = {
  name: string;
  clock: Clock;
  handleStartTime: (clock: Clock) => void;
  handleStopTime: (clock: Clock) => void;
};

const ChildClockRow = ({ name, clock, handleStartTime, handleStopTime }: ChildClockRowProps) => {
  const handleToggleClock = (clock: Clock) => {
    if (!clock.clockTimeUpdatedAt) {
      handleStartTime(clock);
    } else {
      handleStopTime(clock);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <div className="flex-auto min-w-0 font-semibold">{name}</div>
      <div>
        <ChildClock clock={clock} handleToggleClock={handleToggleClock} />
      </div>
    </div>
  );
};

export default Hockey;
